import { DASHBOARD_ROUTE_PERMISSIONS } from './dashboardRouteContract.js';

// Security-sensitive routes retain a code-owned floor even when Firestore menu
// data is missing or misconfigured. Menu documents may control navigation
// visibility, but they cannot make these routes more permissive.
export const EXPLICIT_ROUTE_PERMISSIONS = Object.freeze({
  ...DASHBOARD_ROUTE_PERMISSIONS,
  '/people': Object.freeze(['teacher', 'admin', 'dev']),
});

/**
 * Build route permissions map from menu items dynamically
 * This ensures route permissions match the menu visibility rules
 * @param {Array} menuItems - Menu items with roles configuration
 * @returns {Object} Map of route paths to allowed roles
 */
function buildRoutePermissionsFromMenu(menuItems = []) {
  const permissions = {};

  const processItems = (items) => {
    items.forEach(item => {
      if (item.href) {
        // Extract roles that have access (roles where value is true)
        const allowedRoles = Object.entries(item.roles || {})
          .filter(([_role, hasAccess]) => hasAccess === true)
          .map(([role]) => role);

        if (allowedRoles.length > 0) {
          permissions[item.href] = allowedRoles;
        }
      }

      // Process child items recursively
      if (item.children && item.children.length > 0) {
        processItems(item.children);
      }
    });
  };

  processItems(menuItems);
  return permissions;
}

/**
 * Get route permissions from Firestore menu items, then apply the immutable
 * code-owned floor for security-sensitive routes.
 * Routes without either kind of permission remain available to authenticated users.
 */
export function getRoutePermissions(menuItems) {
  return {
    ...buildRoutePermissionsFromMenu(menuItems),
    ...EXPLICIT_ROUTE_PERMISSIONS,
  };
}

/**
 * Check if a role has access to a specific route path
 * Firestore menu data controls ordinary routes; explicit permissions protect
 * security-sensitive routes when menu data is missing or too permissive.
 * @param {string} path - The route path to check
 * @param {string} role - The user's current role
 * @param {Array} menuItems - Menu items from Firestore (flat array with all items including hidden routes)
 * @returns {boolean} - Whether the role has access
 */
export function hasRouteAccess(path, role, menuItems = null) {
  // Merge Firestore menu data with the code-owned security floor.
  const hasMenuItems = menuItems && Array.isArray(menuItems) && menuItems.length > 0;

  // Build permissions from menu items if available
  const permissions = getRoutePermissions(hasMenuItems ? menuItems : []);

  // Find matching route (exact match or parent path)
  const routeKey = Object.keys(permissions)
    .sort((left, right) => right.length - left.length)
    .find(route => path === route || path.startsWith(route + '/'));

  // If no permission defined, allow access (public or unrestricted route)
  // This allows routes that aren't in the menu system to be accessible
  if (!routeKey) {
    return true;
  }

  const allowed = permissions[routeKey].includes(role);

  // Only log denials for debugging
  if (!allowed) {
    console.error(`[RoutePermissions] 🚫 DENIED - Role "${role}" cannot access ${path} (allowed: ${permissions[routeKey].join(', ')})`);
  }

  return allowed;
}

/**
 * Get allowed roles for a specific route
 * @param {string} path - The route path
 * @param {Object} customPermissions - Optional custom permissions map
 * @returns {string[]} - Array of allowed role names, or null if no restrictions
 */
export function getAllowedRoles(path, customPermissions = null) {
  const permissions = customPermissions || getRoutePermissions();

  const routeKey = Object.keys(permissions)
    .sort((left, right) => right.length - left.length)
    .find(route => path === route || path.startsWith(route + '/'));

  return routeKey ? permissions[routeKey] : null;
}

/**
 * Update route permissions with fresh menu data
 * Call this when menu items are updated in Firestore
 * @param {Array} menuItems - Updated menu items
 */
export function updateRoutePermissions(menuItems) {
  return getRoutePermissions(menuItems);
}
